import { AfterViewInit, ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DzSearchFilterEvent } from '@blk/daisy/combinations/search/search.model';
import { DzModalService } from '@blk/daisy/modules/modals';
import { GlobalAnalyticsMapperUtil, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ApplicationPathConstants } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { AuthenticationManageService } from '@ddt-web/core';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { InjectQueryParam } from '@ddt-web/generic-utils';
import {
	FamiliesUtil,
	FamilyListComponent,
	FamilyTypes,
	FilterSelectionState,
	FiltersOptions,
	MiniCompareDialogComponent,
	MiniCompareUtil,
	ModelDropdown,
	ModelRowData,
	ModelsBrowseAllPdfReportsDownloadService,
	ModelsBrowseFilterTransformUtil,
	ModelsBrowseStoreConfig,
	ModelsExperienceFilterChangeConfig,
	ModelsExperienceFilters,
	ModelsHoldingsExcelDownloadService
} from '@ddt-web/models-browsing';
import { ApplicationStateStoreProxyService, PerformanceState } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { MeQueryParamKey } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as cloneDeep from 'lodash/cloneDeep';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import * as partition from 'lodash/partition';
import * as uniqBy from 'lodash/uniqBy';
import { combineLatest, Observable, of } from 'rxjs';
import { first, map, take } from 'rxjs/operators';

@UntilDestroy()
@Component({
	selector: 'me-find-and-compare',
	templateUrl: './find-compare.component.html',
	styleUrls: ['./find-compare.component.scss']
})
export class FindCompareComponent implements OnInit, AfterViewInit {

	@InjectQueryParam(MeQueryParamKey.SORT_BY_TRADE_DATE) private readonly sortByLatestTrade: boolean = false;
	@InjectQueryParam(MeQueryParamKey.USER_FILTER_NAME) private readonly userFilterName: string;
	@InjectQueryParam(MeQueryParamKey.SELECTED_MODELS_DATA) private readonly selectedModels: string;
	@InjectQueryParam(MeQueryParamKey.TRIGGER_SIGN_IN) private readonly triggerSignIn: boolean;
	@InjectQueryParam(MeQueryParamKey.ROUTED_FROM) private readonly routedFrom: string;
	@InjectQueryParam(MeQueryParamKey.DOWNLOAD_ALL_PDF) private readonly downloadAllPdf: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_DOWNLOAD_HOLDINGS_BUTTON_ON_FIND_AND_COMPARE) public readonly showDownloadButton: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_DOWNLOAD_HOLDINGS_MODULARITY) public readonly enableDownloadHoldingsModularity: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_DOWNLOAD_ALL_PDF_BUTTON) public readonly showDownloadAllPdfButton: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_SEARCH_BUTTON) public readonly showSearchButton: boolean = true;
	@InjectConfig(SharedConfigKey.EnableCustomFee) private readonly isCustomFeeEnabled: boolean = true;

	@ViewChildren(FamilyListComponent) public familyListRefs: QueryList<any>;

	public filtersList: FiltersOptions[];
	public familyIdsPerCategory: { [categoryId: string]: Observable<string[]> } = {};
	public familyModelsPerCategory: { [categoryId: string]: { [familyId: string]: string[] } } = {};
	public groupIdNames: { [categoryId: string]: string } = {};
	public categories: string[] = [];
	public isLoading: boolean = true;
	public selectedModelsToCompare: Array<ModelRowData> = [];
	public dropdownFiltersList: ModelsExperienceFilters[] = [];
	public highlightedFiltersList: ModelsExperienceFilters[] = [];
	public families: any;
	public selectedModelsList: ModelDropdown[] = [];
	public highlightedFilters: ModelsExperienceFilters[] = [];
	public dropdownFilters: ModelsExperienceFilters[] = [];
	public selectedDropdownFilters: ModelsExperienceFilters[] = [];
	public selectedHighlightedFilter: ModelsExperienceFilters;
	public selectedDropdownFiltersFromUrl: ModelsExperienceFilters[] = [];
	public selectedHighlightedFiltersFromUrl: ModelsExperienceFilters[] = [];
	public selectedFilters: ModelsExperienceFilters[] = [];
	public modelCount: number = 0;
	public hideFiltersAndCount: boolean = true; // to be hidden initially and get showed up condition is true. If false assigend will show up and hide which is not good for user experience
	public isAuthenticated$: Observable<boolean> = of(false);
	public selectedHighlightedFilterConfig: FilterSelectionState[];
	public isAuthenticated: boolean = false;
	public tealiumConstants = TealiumConstants;
	private readonly HIGHLIGHTED = 'highlighted';
	private isBlkModelsAvailable: boolean = true;
	public showMobileFilters: boolean = false;
	public showMobileFilterWarningMsg: boolean = true;
	public customAdvisoryFee$: Observable<number>;
	public selectedDropdownFiltersFromMobileModal: ModelsExperienceFilters[] = [];
	public searchText: string = '';
	public hasCMSModels: boolean = false;
	public familyTypes = FamilyTypes;

	constructor(
		private storeService: StoreService,
		private modalService: DzModalService,
		private router: Router,
		private cdr: ChangeDetectorRef,
		private modelsHoldingsExcelDownloadService: ModelsHoldingsExcelDownloadService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private activatedRoute: ActivatedRoute,
		private authManagerService: AuthenticationManageService,
		private modelsBrowseAllPdfReportsDownloadService: ModelsBrowseAllPdfReportsDownloadService
	) { }

	public ngOnInit(): void {
		this.isAuthenticated$ = this.authManagerService.getUserAuthenticated();
		GlobalAnalyticsMapperUtil.setPageName(TealiumConstants.MODELS_EXPERIENCE_EVENTS.FIND_AND_COMPARE_PAGE);

		let selectedModelsListFromUrl = [];
		const routedFromUrl = this.routedFrom;
		if (this.selectedModels) {
			selectedModelsListFromUrl = ModelsBrowseFilterTransformUtil.transformUrlToSelectedModels(this.selectedModels);
		}

		this.isAuthenticated$ = this.authManagerService.getUserAuthenticated();
		this.customAdvisoryFee$ = this.applicationStateStoreProxyService.getPerformanceState().pipe(map((state: PerformanceState) => state.customAdvisoryFee));
		this.isAuthenticated$.pipe(take(1)).subscribe((authenticated) => {
			this.isAuthenticated = authenticated;
			if (!authenticated && this.triggerSignIn && !this.selectedModels) {
				this.authManagerService.initializeOktaSignIn();
			}
		});

		this.storeService.selectAll(ModelsBrowseStoreConfig.families).pipe(
			first(families => !families.isLoading)
		).subscribe((families) => {
			const entities = families.entities;
			const { familyModelsPerCategory, groupIdNames, categories, familyIdsPerCategory } = FamiliesUtil.generateCategoriesModelLevel(families);
			this.familyModelsPerCategory = familyModelsPerCategory;
			this.groupIdNames = groupIdNames;
			this.categories = categories;
			this.hasCMSModels = this.categories.length > 1 ? true : false;
			this.familyIdsPerCategory = familyIdsPerCategory;
			this.isBlkModelsAvailable =
				categories.includes(SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP) ||
				categories.includes(SharedApplicationConstants.MODEL_FAMILY_AU.BLACKROCK_FAMILY_GROUP);

			// Update store if we have allRequestParams queryParams in the url
			if (this.isBlkModelsAvailable && selectedModelsListFromUrl?.length) {
				const selectedModelsData = MiniCompareUtil.flattenSelectedModelsDataFromStore(selectedModelsListFromUrl, entities);
				// Update the meState store to persist selection on homepage
				this.applicationStateStoreProxyService.updateMeSelectedModelsData(
					{ selectedModels: selectedModelsData, routedFrom: routedFromUrl || ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.HOME }
				);
			}
			this.hideFiltersAndCount = !this.isBlkModelsAvailable || this.categories.length > 1;
			this.families = families;
			this.isLoading = false;
		});

		this.storeService.selectAll(ModelsBrowseStoreConfig.filterConfig).pipe(first(filters => !filters.isLoading)).subscribe((filtersList) => {
			const filters = Object.values(filtersList?.entities ?? {});
			const [highlightedFiltersList, dropdownFiltersList] = partition(Object.values(filters), filter => filter?.meFilterGroup === this.HIGHLIGHTED);
			this.dropdownFilters = cloneDeep(dropdownFiltersList);
			this.highlightedFilters = cloneDeep(highlightedFiltersList);
		});

		// queryParams Change detection to update filters based on userFilterName queryParam
		this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((queryParams) => {
			if (queryParams['userFilterName']) {
				this.updateSelectedFiltersFromUrl();
			}
		});

		this.tealiumAnalyticsService.triggerToolStart(TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE);
	}

	public ngAfterViewInit(): void {
		combineLatest([
			this.applicationStateStoreProxyService.getMeSelectedModelsData$(),
			this.familyListRefs.changes
		]).pipe(
			first(([meState, refs]) => !meState.isLoading && !!refs)
		).subscribe(([meState, refs]) => {
			if (meState?.modalData?.routedFrom === ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.HOME ||
				meState?.modalData?.routedFrom === ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.FIND_AND_COMPARE) {
				const modalDataFromStore = meState?.modalData?.selectedModels;
				const familyListRef = refs?.first;
				const uniqueFamilies = uniqBy(modalDataFromStore, 'familyId');
				uniqueFamilies.forEach(family => {
					const familyRow = familyListRef.familyList.filter(row => row?.familyId?.toString() === family?.['familyId']);
					if (familyRow?.length) {
						familyListRef.onToggleAccordion(familyRow?.[0]);
					}
				});
				modalDataFromStore?.forEach(modelRow => {
					this.setSelectedModelsToCompare(modelRow, meState?.modalData?.routedFrom);
				});

				// Open Dialog if blk models are available
				if (this.isBlkModelsAvailable) {
					this.modalService.open(MiniCompareDialogComponent, { showClose: true, size: 'lg' });
				}
			}
		});
		let queryParams = { 'allRequestParams': null, 'routedFrom': null };
		if (this.hideFiltersAndCount) {// cms tab active on initial load remove filters from home page if its there
			this.selectedFilters = [];
			queryParams = Object.assign({}, { ...queryParams, 'userFilterName': null });
		}
		// Remove allRequestParams from URL in case someone hits using deeplinked url, it should not update meState store
		this.router.navigate([], {
			relativeTo: this.activatedRoute,
			queryParams,
			queryParamsHandling: 'merge',
			replaceUrl: true
		});
	}

	public setSelectedModelsToCompare(eventData, routedFrom?: string): void {
		const flattenedData = isNil(routedFrom) ? MiniCompareUtil.flattenSelectedModelsDataFromStore([eventData]) : [eventData];
		const portfolioId = flattenedData?.[0]?.modelId;
		if (eventData.checked) {
			this.selectedModelsToCompare = [...this.selectedModelsToCompare, ...flattenedData];
		} else {
			this.selectedModelsToCompare = this.selectedModelsToCompare.filter(item => {
				return item?.modelId !== portfolioId;
			});
		}
	}

	public searchKeyword(searchValue: DzSearchFilterEvent): void {
		this.searchText = searchValue.searchText.toLowerCase();
	}

	public getSelectedFilters(highlightedFiltersConfig: ModelsExperienceFilterChangeConfig): void {
		const latestSelectedFilter = highlightedFiltersConfig.selectedHighlightedFilter;

		if (highlightedFiltersConfig.selectedFilterConfig) {
			let selectedFilterConfig;

			if (!latestSelectedFilter || !this.selectedHighlightedFilter) {
				selectedFilterConfig = [highlightedFiltersConfig.selectedFilterConfig];
			} else if (latestSelectedFilter.meFilterId !== this.selectedHighlightedFilter.meFilterId) {
				// if different filter selected, deselect previous selected filter and select current selected filter
				selectedFilterConfig = [highlightedFiltersConfig.selectedFilterConfig];
				selectedFilterConfig.push({
					id: this.selectedHighlightedFilter.meFilterId,
					checked: false
				});
			}
			this.selectedHighlightedFilterConfig = selectedFilterConfig;
		} else {
			this.selectedHighlightedFilterConfig = undefined;
		}
		this.selectedHighlightedFilter = latestSelectedFilter;
	}

	public getSelectedDropdownFilters(dropdownFilters: ModelsExperienceFilters[]): void {
		this.selectedDropdownFilters = dropdownFilters;
		this.updateUrlFromFilters();
	}

	public applySelectedFilters(): void {
		this.selectedDropdownFilters = [...this.selectedDropdownFiltersFromMobileModal];
		this.selectedDropdownFiltersFromMobileModal = [];
		this.updateUrlFromFilters();
		this.showMobileFilters = !this.showMobileFilters;
	}

	public onOpenMiniCompareDialog(): void {
		const selectedModelsId = this.selectedModelsToCompare.map((list) => list?.['modelId']);
		const event = TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_EXPERIENCE_APP_NAME + ':' + TealiumConstants.MODELS_EXPERIENCE_EVENTS.FIND_AND_COMPARE_PAGE + `:mini compare bar:button click:${selectedModelsId}`;
		this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(event, {
			toolName: TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE
		}));
		const selectedModelsParam = ModelsBrowseFilterTransformUtil.getSelectedModelsQueryParam(this.selectedModelsToCompare);
		const params = {
			'allRequestParams': !isEmpty(selectedModelsParam) ? selectedModelsParam.join('$') : null,
			'routedFrom': ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.FIND_AND_COMPARE
		};
		this.applicationStateStoreProxyService.updateMeSelectedModelsData(
			{ selectedModels: this.selectedModelsToCompare, routedFrom: ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.FIND_AND_COMPARE }
		);
		if (!this.isAuthenticated) {
			this.authManagerService.initializeOktaSignIn({ 'redirectUrlPath': '/' + ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.FIND_AND_COMPARE, ...params });
		} else {
			this.modalService.open(MiniCompareDialogComponent, { showClose: true, size: 'lg' });
		}

	}

	public onRemoveSelectedModelsData(selectedModels: Array<ModelRowData>): void {
		this.selectedModelsToCompare = selectedModels;
	}

	public showFilteredModelsCount(count: number) {
		this.modelCount = count;
		this.cdr.detectChanges();
	}

	public handleFiltersVisibility(selectedTab: { index: number; title: string }): void {
		this.hideFiltersAndCount = (
			selectedTab.title === SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP_TITLE ||
			selectedTab.title.toLowerCase().includes('blackrock')
		) ? false : true;
		this.updateFiltersBasedOnSelectedTab();
	}

	private updateFiltersBasedOnSelectedTab() {
		if (this.hideFiltersAndCount) { // cms tab
			this.selectedFilters = [];
			this.router.navigate([], { queryParams: { 'userFilterName': null }, queryParamsHandling: 'merge' });
		} else {
			this.updateUrlFromFilters(); // to persist filters
		}
	}

	public onDownloadHoldingsClick(familyType: string): void {
		this.tealiumAnalyticsService.triggerToolCompletion(null, TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE, TealiumConstants.MODELS_EXPERIENCE_EVENTS.DOWNLOAD_HOLDINGS);
		this.modelsHoldingsExcelDownloadService.downloadHoldings(null, this.sortByLatestTrade, familyType).pipe(first()).subscribe();
	}

	public updateSortByLatestTrade() {
		const sortByTradeDate = !this.sortByLatestTrade;
		const queryParams = { sortByTradeDate };
		const event = TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_EXPERIENCE_APP_NAME + ':' + TealiumConstants.MODELS_EXPERIENCE_EVENTS.FIND_AND_COMPARE_PAGE + `:sort by latest trade:${sortByTradeDate}`;
		this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(event, {
			toolName: TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE
		}));
		this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
	}

	/**
	 * function to update url on the basis of selected filters
	 */
	private updateUrlFromFilters(): void {
		this.selectedFilters = [...this.selectedDropdownFilters];
		if (!!this.selectedHighlightedFilter) {
			this.selectedFilters.push(this.selectedHighlightedFilter);
		}
		const filterQueryParams = ModelsBrowseFilterTransformUtil.getFilterQueryParam(this.selectedFilters);
		const params = { 'userFilterName': !isEmpty(filterQueryParams) ? filterQueryParams.join('$') : null };
		this.router.navigate([], { queryParams: params, queryParamsHandling: 'merge' });
	}

	/**
	 * function to update filters from the url params on load
	 */
	private updateSelectedFiltersFromUrl(): void {

		if (isEmpty(this.userFilterName)) {
			this.selectedFilters = [];
			return;
		}
		if (!!this.userFilterName) {
			const selectedFilterList = ModelsBrowseFilterTransformUtil.transformUrlToFilterList(this.userFilterName);
			this.selectedFilters = selectedFilterList;
		}

		this.selectedHighlightedFiltersFromUrl = this.selectedFilters.filter((filter) => filter.meFilterGroup === this.HIGHLIGHTED);
		this.selectedDropdownFiltersFromUrl = this.selectedFilters.filter((filter) => filter.meFilterGroup !== this.HIGHLIGHTED);
		this.selectedHighlightedFilter = this.selectedHighlightedFiltersFromUrl[0];
	}

	public clearAllFilters(): void {
		const eventName = TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_EXPERIENCE_APP_NAME + ':' + TealiumConstants.MODELS_EXPERIENCE_EVENTS.FIND_AND_COMPARE_PAGE + `clearAllFilterSelection`;
		this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(eventName, { toolName: TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE }));
		this.selectedFilters = [];
		this.searchText = '';
		this.selectedDropdownFilters = [];
		this.selectedHighlightedFilter = null;
		this.selectedDropdownFiltersFromUrl = [];
		this.selectedHighlightedFiltersFromUrl = [];
		this.router.navigate([], { queryParams: { 'userFilterName': null }, queryParamsHandling: 'merge' });
	}

	public onDownloadAllPdfReportClick(excludePerformance: boolean = false): void {
		this.modelsBrowseAllPdfReportsDownloadService.downloadAllPdfReport(this.families, this.categories, this.familyModelsPerCategory, excludePerformance);
	}
}
